import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AiAssistantData {
  firstName: string;
  userRole: string;
  brandName: string;
  totalSquadMembers: number;
  totalTasks: number;
  totalSOPs: number;
}

export const useAiAssistantData = (
  currentBrandId: string | null,
  user: any,
  foundationalTasksCount: number,
  growthTasksCount: number,
  recurringTasksCount: number,
  sopTasksCount: number
) => {
  const [assistantData, setAssistantData] = useState<AiAssistantData>({
    firstName: '',
    userRole: '',
    brandName: '',
    totalSquadMembers: 0,
    totalTasks: 0,
    totalSOPs: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAssistantData = async () => {
      if (!user || !currentBrandId) {
        setIsLoading(false);
        return;
      }

      try {
        // Get user profile for first name
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', user.id)
          .single();

        // Extract first name from full_name or email
        let firstName = 'there';
        if (profile?.full_name) {
          firstName = profile.full_name.split(' ')[0];
        } else if (profile?.email) {
          firstName = profile.email.split('@')[0];
        } else if (user.email) {
          firstName = user.email.split('@')[0];
        }

        // Get brand information
        const { data: brandData } = await supabase
          .from('brands')
          .select('name, user_id')
          .eq('id', currentBrandId)
          .single();

        // Determine user role
        let userRole = 'member';
        if (brandData?.user_id === user.id) {
          userRole = 'owner';
        } else {
          const { data: accessData } = await supabase
            .from('user_brand_access')
            .select('role')
            .eq('brand_id', currentBrandId)
            .eq('user_id', user.id)
            .single();

          if (accessData?.role) {
            userRole = accessData.role;
          }
        }

        // Get squad members count
        const { data: squadMembers } = await supabase
          .from('growth_squad')
          .select('id')
          .eq('brand_id', currentBrandId);

        // Calculate totals
        const totalTasks = foundationalTasksCount + growthTasksCount + recurringTasksCount;

        setAssistantData({
          firstName,
          userRole,
          brandName: brandData?.name || 'your brand',
          totalSquadMembers: squadMembers?.length || 0,
          totalTasks,
          totalSOPs: sopTasksCount
        });

      } catch (error) {
        console.error('Error fetching assistant data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssistantData();
  }, [currentBrandId, user, foundationalTasksCount, growthTasksCount, recurringTasksCount, sopTasksCount]);

  return { assistantData, isLoading };
};